/**
 * The palette and the handful of canvas primitives every in-world panel paints
 * with.
 *
 * The colours restate the custom properties at the top of `ui/styles.css`. A
 * canvas cannot read those without a `getComputedStyle` per repaint, and these
 * panels are painted on the frame thread, so they are copied here — the same
 * trade `ui/map.ts` already makes for the same reason. If the stylesheet's
 * amber moves, move it here too.
 *
 * Everything below works in canvas pixels. A panel decides how many pixels it
 * is worth (see `panel.ts`) and hands the drawing code a context of that size,
 * so nothing here has to know how large the quad is in metres.
 */

export const AMBER = '#e8d98a';
export const AMBER_DIM = '#a4954e';
export const AMBER_FAINT = '#6f6538';
export const BAD = '#c9564a';
export const GOOD = '#86b06a';

/**
 * Panel ground. Darker and more opaque than the DOM's `--panel`: a DOM overlay
 * sits on a dimmed render, an in-world quad sits on the lit room itself, and
 * amber-on-translucent-black over a lit yellow wall is unreadable.
 */
export const INK = 'rgba(7, 6, 5, 0.93)';
export const LINE = 'rgba(164, 149, 78, 0.34)';
export const LINE_FAINT = 'rgba(164, 149, 78, 0.15)';

/** The same stack `ui/map.ts` paints with; no family name contains a space. */
export const FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export interface TextStyle {
  /** Cap-to-cap size in canvas pixels. */
  px: number;
  color?: string;
  align?: 'left' | 'center' | 'right';
  /**
   * Extra advance between glyphs, in canvas pixels. The DOM UI tracks
   * everything out by 0.06em; headings get more.
   */
  tracking?: number;
  weight?: 'normal' | 'bold';
  alpha?: number;
}

/**
 * Fill a panel: near-black ground and a hairline rule around it.
 *
 * Square corners and no shadow, deliberately — see the header of `styles.css`.
 * The border is what makes a floating quad read as a thing rather than as a
 * smear of text hanging in the room.
 */
export function face(c: CanvasRenderingContext2D, w: number, h: number, border = LINE): void {
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, w, h);
  c.fillStyle = INK;
  c.fillRect(0, 0, w, h);
  if (!border) return;
  const t = Math.max(1, Math.round(w * 0.0022));
  c.strokeStyle = border;
  c.lineWidth = t;
  c.strokeRect(t / 2, t / 2, w - t, h - t);
}

/** Clear to nothing. For panels that are only text over the room. */
export function clear(c: CanvasRenderingContext2D, w: number, h: number): void {
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, w, h);
}

function apply(c: CanvasRenderingContext2D, s: TextStyle): void {
  c.font = `${s.weight === 'bold' ? '600 ' : ''}${Math.round(s.px)}px ${FONT}`;
  c.textBaseline = 'middle';
  c.textAlign = 'left';
  c.fillStyle = s.color ?? AMBER;
  c.globalAlpha = s.alpha ?? 1;
}

/**
 * Width of `s` including tracking, in canvas pixels.
 *
 * Measured glyph by glyph rather than trusting a monospace advance: the stack
 * falls back to whatever the headset's browser calls `monospace`, and a
 * proportional fallback would silently mis-centre every heading.
 */
export function measure(c: CanvasRenderingContext2D, s: string, style: TextStyle): number {
  apply(c, style);
  const tracking = style.tracking ?? 0;
  if (tracking === 0) return c.measureText(s).width;
  let w = 0;
  for (const ch of s) w += c.measureText(ch).width + tracking;
  return Math.max(0, w - tracking);
}

/**
 * Draw one line. `y` is the vertical *centre* of the line, which is what panel
 * layout actually wants; baselines are an implementation detail.
 *
 * Tracking is applied by hand rather than through `ctx.letterSpacing`, which
 * is Chromium-only and would leave the Vision Pro's Safari drawing headings
 * with none. Returns the width drawn.
 */
export function text(c: CanvasRenderingContext2D, s: string, x: number, y: number, style: TextStyle): number {
  const width = measure(c, s, style);
  const align = style.align ?? 'left';
  let cursor = align === 'center' ? x - width / 2 : align === 'right' ? x - width : x;
  const tracking = style.tracking ?? 0;
  if (tracking === 0) {
    c.fillText(s, cursor, y);
  } else {
    for (const ch of s) {
      c.fillText(ch, cursor, y);
      cursor += c.measureText(ch).width + tracking;
    }
  }
  c.globalAlpha = 1;
  return width;
}

/**
 * The largest size at or below `style.px` that fits `s` into `maxWidth`.
 *
 * Panels are fixed quads and the game writes its own prompt labels, so an
 * unlucky one ("Descend the gap in the wall") would otherwise run off the edge.
 * Shrinking beats wrapping for a single-line readout: two lines of prompt in
 * the corner of the eye is worse than one slightly smaller one.
 */
export function fitPx(c: CanvasRenderingContext2D, s: string, maxWidth: number, style: TextStyle): number {
  let px = style.px;
  for (let i = 0; i < 8; i++) {
    const w = measure(c, s, { ...style, px });
    if (w <= maxWidth || px <= style.px * 0.55) break;
    px *= Math.max(0.72, maxWidth / w);
  }
  return px;
}

/** Greedy word wrap. Returns the lines; the caller owns line spacing. */
export function wrap(c: CanvasRenderingContext2D, s: string, maxWidth: number, style: TextStyle): string[] {
  apply(c, style);
  const words = s.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (line && measure(c, next, style) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * A segmented fill bar, matching the DOM meter's read: a dim track with a
 * brighter fill notched into cells. The notches are what stop a smooth bar from
 * looking like a progress bar in a game menu.
 */
export function bar(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  v01: number,
  color = AMBER,
): void {
  c.globalAlpha = 1;
  const cells = 24;
  const pitch = w / cells;
  const gap = Math.max(1, Math.round(pitch * 0.28));
  const seg = pitch - gap;
  const lit = Math.max(0, Math.min(1, v01)) * cells;
  for (let i = 0; i < cells; i++) {
    c.fillStyle = i < lit ? color : LINE_FAINT;
    c.fillRect(x + i * pitch, y, seg, h);
  }
}

/** A hairline rule across a panel. */
export function rule(c: CanvasRenderingContext2D, x: number, y: number, w: number, color = LINE_FAINT): void {
  c.globalAlpha = 1;
  c.fillStyle = color;
  c.fillRect(x, y, w, Math.max(1, Math.round(w * 0.0015)));
}
